import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'

import { showError, showSuccess } from '@/lib/dialogHelper'
import CustomerPicker from '@/widgets/CustomerPicker'
import CustomerCard from '@/widgets/CustomerCard'

import MembershipsService from '@/services/MembershipsService'
import ConsumptionHistoryTable from './ConsumptionHistoryTable'
import LoadingIndicator from '@/widgets/LoadingIndicator'

import { buildMembershipPayload } from '../add/submitMembership'

const mapHistory = list =>
	(Array.isArray(list) ? list : []).map((entry, idx) => ({
		id: entry?._id || entry?.id || entry?.consumeId || idx,
		date: entry?.date || entry?.dateLocal || entry?.consumedAt || entry?.created,
		dateLocal: entry?.dateLocal,
		service: entry?.service || entry?.title || entry?.description || 'Usage',
	}))

export default function MembershipEdit() {
	const { id } = useParams()
	const navigate = useNavigate()

	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [savingHistory, setSavingHistory] = useState(false)
	const [error, setError] = useState(null)

	const [form, setForm] = useState({
		customerId: '',
		customerName: '',
		type: '',
		status: '',
		remainingUsage: '',
		expiryDate: '',
	})

	const [history, setHistory] = useState([])

	const setField = useCallback((name, value) => {
		setForm(prev => ({ ...prev, [name]: value }))
	}, [])

	const hydrateFromMembership = useCallback(data => {
		if (!data) return
		setForm(prev => ({
			...prev,
			customerId: data.customerId,
			customerName: data.customerName || getCustomerName(data.customerId || data.customer || {}),
			type: String(data.type || '').toLowerCase(),
			status: String(data.status || '').toLowerCase(),
			remainingUsage: data.remainingUsage ?? '',
			expiryDate: moment(data.expiryDate || data.expiry || data.expirydate).format('YYYY-MM-DD'),
		}))
		setHistory(mapHistory(data.consumptionDates || data.consumptionHistory || []))
	}, [])

	const loadMembership = useCallback(
		async membershipId => {
			setLoading(true)
			setError(null)
			const { data, error: apiError } = await MembershipsService.getMembershipById(membershipId)
			// console.log(`TCL -> index.jsx:69 -> MembershipEdit -> data:`, data)
			if (apiError) {
				setError(apiError)
			} else {
				hydrateFromMembership(data)
			}
			setLoading(false)
		},
		[hydrateFromMembership]
	)

	useEffect(() => {
		let alive = true
		;(async () => {
			if (alive) {
				await loadMembership(id)
			}
		})()
		return () => {
			alive = false
		}
	}, [id, loadMembership])

	const handleCustomerSelect = useCallback(
		selectedCustomer => {
			setField('customerId', selectedCustomer._id || '')
			setField('customerName', `${selectedCustomer.name || ''}`.trim())
		},
		[setField]
	)

	const handleCustomerClear = () => {
		console.log('Clearing customer selection')
		setField('customerId', '')
		setField('customerName', '')
	}

	const handleSubmit = useCallback(
		async e => {
			e?.preventDefault()
			if (submitting) return

			setSubmitting(true)
			setError(null)

			try {
				const payload = buildMembershipPayload(form)
				const { success, error: apiError } = await MembershipsService.updateMembership(id, payload)
				if (!success) throw new Error(apiError || 'Failed to update membership')
				navigate(`/memberships/details/${id}`)
			} catch (err) {
				setError(err.message || 'Failed to update membership')
				showError(err.message || 'Failed to update membership')
			} finally {
				setSubmitting(false)
			}
		},
		[form, submitting, id, navigate]
	)

	const handleRemoveHistory = useCallback(
		async idx => {
			const entry = history[idx]
			if (!entry || !entry.id) return
			setSavingHistory(true)
			try {
				const res = await MembershipsService.unconsumeMembership(id, entry.id)
				if (!res.success) throw new Error(res.error || 'Failed to remove usage')
				const { data, error: reloadErr } = await MembershipsService.getMembershipById(id)
				if (reloadErr) throw new Error(reloadErr)
				hydrateFromMembership(data)
				showSuccess('Usage entry removed.')
			} catch (err) {
				showError(err.message || 'Failed to remove usage')
			} finally {
				setSavingHistory(false)
			}
		},
		[history, id, hydrateFromMembership]
	)

	const remainingUsageLabel = useMemo(() => form.remainingUsage ?? '—', [form.remainingUsage])

	if (loading) {
		return <LoadingIndicator />
	}

	// console.log('Rendering MembershipEdit with form:', form)

	return (
		<div className="max-w-5xl mx-auto px-0 sm:px-6 lg:px-8 space-y-6">
			<form className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				<div className="px-4 sm:px-6 py-6 border-b border-gray-200">
					<h2 className="text-2xl font-bold text-gray-900">Edit Membership</h2>
					<p className="text-sm text-gray-500 mt-1">
						Customer: <span className="font-medium text-gray-900"> {form.customerName || '—'} </span>
					</p>
				</div>

				{error && (
					<div className="mx-4 sm:mx-6 mt-6 rounded-lg bg-red-50 p-4 border border-red-200 text-sm text-red-700">
						{error}
					</div>
				)}

				<div className="px-0 sm:px-6 py-6 space-y-6">
					{/* Customer */}
					{!form.customerId && <CustomerPicker onCustomerSelect={handleCustomerSelect} />}
					{form.customerId && <CustomerCard customerId={form.customerId} handleChange={handleCustomerClear} />}

					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 sm:px-0">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
							<select
								value={form.type}
								onChange={e => setField('type', e.target.value)}
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 bg-white">
								<option value="" disabled>
									Select type
								</option>
								<option value="basic">Basic</option>
								<option value="premium">Premium</option>
								<option value="vip">VIP</option>
							</select>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
							<select
								value={form.status}
								onChange={e => setField('status', e.target.value)}
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 bg-white">
								<option value="" disabled>
									Select status
								</option>
								<option value="active">Active</option>
								<option value="suspended">Suspended</option>
								<option value="expired">Expired</option>
								<option value="cancelled">Cancelled</option>
							</select>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Remaining usage</label>
							<input
								type="number"
								min={0}
								value={form.remainingUsage}
								onChange={e => setField('remainingUsage', e.target.value)}
								onWheel={e => e.currentTarget.blur()}
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
								placeholder="e.g. 5"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">Expiry date</label>
							<input
								type="date"
								value={form.expiryDate}
								onChange={e => setField('expiryDate', e.target.value)}
								className="block w-full rounded-lg border-0 py-2.5 px-3.5 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
							/>
						</div>
					</div>
				</div>

				<div className="px-4 sm:px-6 py-6 border-t border-gray-200 flex gap-3">
					<button
						type="button"
						disabled={submitting}
						onClick={handleSubmit}
						className={`px-5 py-2 rounded text-white ${submitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
						{submitting ? 'Saving…' : 'Save changes'}
					</button>
					<button
						type="button"
						onClick={() => navigate('/memberships')}
						className="px-5 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50">
						Cancel
					</button>
				</div>
			</form>

			<div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				<div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between">
					<div>
						<h3 className="text-lg font-semibold text-gray-900">Consumption history</h3>
						<p className="text-sm text-gray-500">Remaining usage: {remainingUsageLabel}</p>
					</div>
				</div>
				<div className="py-4">
					<ConsumptionHistoryTable rows={history} saving={savingHistory} onRemoveRow={handleRemoveHistory} />
				</div>
			</div>
		</div>
	)
}
